import { Flag, History as HistoryIcon, ExternalLink, Pencil, Trash2, Clock, PauseCircle, Film, MessageSquare, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Avatar from "@/components/Avatar";
import TaskTimer from "@/components/TaskTimer";
import TaskCommentsDialog from "@/components/TaskCommentsDialog";
import TaskSidePanel from "@/components/TaskSidePanel";
import { useState } from "react";
import { TID } from "@/constants/testIds";
import { fmtDateTime } from "@/utils/date";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, PRIORITY_ORDER, priorityOf } from "@/constants/status";

const STATUS = Object.fromEntries(STATUS_ORDER.map((k) => [k, { label: STATUS_META[k].label, cls: STATUS_META[k].pill }]));

// Dot swatches shown alongside the label in dropdowns/legends. Match the palette above.
const STATUS_DOT = Object.fromEntries(STATUS_ORDER.map((k) => [k, STATUS_META[k].dot]));

const fmtDate = fmtDateTime;

export default function TaskCard({ task, currentUser, users = [], isManager, onEdit, onDelete, onTogglePriority, onStatusChange, assigneeOnLeaveToday = false }) {
  // Multi-assignee tasks: show the CALLER'S personal status, not the rollup —
  // otherwise marking your own row complete looks like it "reverted" back to
  // in_progress the moment the parent re-renders (the rollup only flips when
  // every assignee has completed).
  const mySlot = (task.assignee_details || []).find((a) => a.user_id === currentUser?.id);
  const isMulti = (task.assignees || []).length > 1;
  const displayStatus = (isMulti && mySlot) ? mySlot.status : task.status;
  const s = STATUS[displayStatus] || STATUS.not_started;
  const deadline = task.deadline;
  const overdue = deadline && new Date(deadline) < new Date() && displayStatus !== "complete";
  const canEdit = isManager || (currentUser && task.assigned_to === currentUser.id) || (currentUser && (task.assignees || []).includes(currentUser.id));
  const assignee = users.find((u) => u.id === task.assigned_to) || { name: task.assigned_to_name, id: task.assigned_to };
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [taskState, setTaskState] = useState(task);
  const commentCount = (taskState.comments || task.comments || []).length;
  const prio = priorityOf(task);
  const prioMeta = PRIORITY_META[prio];

  const changeStatus = (next) => {
    if (!onStatusChange || next === displayStatus) return;
    let reason = "";
    // NOTE: hold_reason is OPTIONAL — we no longer prompt for it here.
    // Users can (optionally) add / edit the reason inside the task's Edit
    // dialog. The status flips instantly, keeping any existing hold_reason
    // that was previously stored on the task.
    if (next === "incomplete") {
      reason = window.prompt("Reason for marking incomplete?", task.incomplete_reason || "");
      if (reason === null) return;
    }
    onStatusChange(task, next, {
      hold_reason: task.hold_reason || "",
      incomplete_reason: reason,
    });
  };

  return (
    <div
      data-testid={TID.taskCard(task.id)}
      className={`group relative rounded-lg border p-3 bg-surface shadow-[var(--shadow-card)] hover:shadow-float hover:-translate-y-px transition-[box-shadow,transform,border-color] ${
        prio === "urgent" ? "border-red-500/50" : prio === "high" ? "border-orange-500/40" : "border-white/10 hover:border-white/20"
      }`}
    >

      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1.5 mb-2">
        <div className="basis-full min-w-0">
          {(task.client || task.project_name) && (
            <div className="text-xs truncate mb-0.5" data-testid={`task-card-client-${task.id}`}>
              <span className="client-line">{task.client}</span>{task.project_name && <span className="text-white/50"> · {task.project_name}</span>}
            </div>
          )}
          <h4 className="font-display font-bold text-[15px] text-white leading-snug flex flex-wrap items-center gap-1.5">
            {prio !== "normal" && (
              <Flag size={13} className={`shrink-0 ${prioMeta.text}`} fill="currentColor" data-testid={`task-priority-flag-${task.id}`} aria-label={`${prioMeta.label} priority`} />
            )}
            {(displayStatus === "not_started" || displayStatus === "incomplete" || displayStatus === "on_hold") && (
              <span
                data-testid={`task-attention-chip-${task.id}`}
                title={`Needs attention · ${STATUS[displayStatus].label}`}
                className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[0.7rem] font-bold shrink-0 ${
                  displayStatus === "incomplete" ? "bg-red-500/25 text-red-200 border border-red-500/50" :
                  displayStatus === "on_hold" ? "bg-orange-500/25 text-orange-200 border border-orange-500/50" :
                  "bg-white/10 text-white/70 border border-white/25"
                }`}
              >!</span>
            )}
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              data-testid={`task-open-panel-${task.id}`}
              title="Open task details"
              className="min-w-[8rem] flex-1 line-clamp-2 break-words text-left hover:text-[#E11D2E] hover:underline underline-offset-2 transition-colors"
            >{task.title}</button>
            {task.day_index && task.day_total > 1 && (
              <span
                data-testid={`task-day-chip-${task.id}`}
                title={`Day ${task.day_index} of ${task.day_total} in this range`}
                className="shrink-0 inline-flex items-center text-[0.65rem] uppercase tracking-widest bg-sky-500/15 border border-sky-400/40 text-sky-200 rounded px-1.5 py-0.5 font-mono"
              >
                D{task.day_index}/{task.day_total}
              </span>
            )}
            {Array.isArray(task.history) && task.history.some(h => h?.kind === "auto_rollover") && (
              <span
                data-testid={`task-rolled-chip-${task.id}`}
                title="Carried forward from an earlier day"
                className="shrink-0 inline-flex items-center text-[0.65rem] uppercase tracking-widest bg-amber-500/15 border border-amber-400/40 text-amber-200 rounded px-1.5 py-0.5 font-mono"
              >
                ↻ Rolled
              </span>
            )}
          </h4>
        </div>
        {onStatusChange && canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`task-status-trigger-${task.id}`}
                title="Change status"
                className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 hover:brightness-110 transition ${s.cls}`}
              >
                {s.label} ▾
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-surface border-white/10 text-white min-w-[160px]" align="end">
              {STATUS_ORDER.map((k) => (
                <DropdownMenuItem
                  key={k}
                  data-testid={`task-status-set-${task.id}-${k}`}
                  onClick={() => changeStatus(k)}
                  className="text-xs cursor-pointer focus:bg-white/10"
                >
                  <span className={`inline-block h-2 w-2 rounded-full mr-2 ${STATUS_DOT[k]}`} />
                  {STATUS[k].label}
                  {displayStatus === k && <span className="ml-auto text-white/40">·</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${s.cls}`}>{s.label}</span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <Avatar user={assignee} size={18} />
        <div className="text-xs text-white/50 truncate">{assignee.name || task.assigned_to_name}</div>
        {assigneeOnLeaveToday && (
          <span
            data-testid={`task-assignee-on-leave-${task.id}`}
            title="Assignee is on leave today"
            className="text-[0.65rem] uppercase tracking-widest rounded px-1 py-0.5 border text-amber-300 bg-amber-500/10 border-amber-500/40 inline-flex items-center gap-1"
          ><Coffee size={9} /> leave</span>
        )}
      </div>

      {task.description && (
        <p className="text-xs text-white/60 leading-relaxed mb-2 line-clamp-2">{task.description}</p>
      )}

      {task.status === "on_hold" && task.hold_reason && (
        <div className="text-xs text-orange-300/90 bg-orange-500/10 border border-orange-500/20 rounded px-2 py-1 mb-2 flex items-start gap-1">
          <PauseCircle size={11} className="mt-[2px] shrink-0" />
          <span className="line-clamp-2">{task.hold_reason}</span>
        </div>
      )}
      {task.status === "incomplete" && task.incomplete_reason && (
        <div className="text-xs text-red-300/90 bg-red-500/10 border border-red-500/20 rounded px-2 py-1 mb-2 line-clamp-2">
          Reason: {task.incomplete_reason}
        </div>
      )}

      {/* Task time tracker — assignee can start/pause/finish; managers see aggregate.
          Hidden for to-do-derived pseudo-tasks (they don't have a real /api/tasks id). */}
      {(canEdit || isManager) && !task._from_todo && (
        <TaskTimer task={task} currentUser={currentUser} isManager={isManager} onStatusChange={onStatusChange} />
      )}

      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs mb-3 mt-2">
        <div className={`flex items-center gap-1 font-mono ${overdue ? "text-red-400" : "text-white/50"}`}>
          <Clock size={11} />
          <span>{fmtDate(deadline)}</span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {task.nas_link && (
            <a href={task.nas_link} target="_blank" rel="noreferrer"
               data-testid={`task-nas-link-${task.id}`}
               className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
               title="Open NAS workfile">
              <ExternalLink size={11} /> NAS
            </a>
          )}
          {task.final_video_link && (
            <a href={task.final_video_link} target="_blank" rel="noreferrer"
               data-testid={`task-final-link-${task.id}`}
               className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
               title="Open final video">
              <Film size={11} /> Final
            </a>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity -mx-1">
        {onEdit && canEdit && (
          <Button
            variant="ghost" size="sm"
            data-testid={TID.taskEditBtn(task.id)}
            title="Edit task"
            onClick={() => onEdit(task)}
            className="h-7 px-1.5 text-white/60 hover:text-white hover:bg-white/5"
          >
            <Pencil size={12} className="mr-1" /> Edit
          </Button>
        )}

        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost" size="sm"
              data-testid={TID.taskHistoryBtn(task.id)}
              title="See every change to this task"
              className="h-7 px-1.5 text-white/60 hover:text-white hover:bg-white/5"
            >
              <HistoryIcon size={12} className="mr-1" /> History
              {task.history?.length > 0 && (
                <span className="ml-1 text-[0.7rem] text-white/40">({task.history.length})</span>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">Task History — {task.title}</DialogTitle>
              <DialogDescription className="text-white/50">
                Every change to this task, oldest to newest.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
              {(!task.history || task.history.length === 0) && (
                <p className="text-sm text-white/40">No changes yet.</p>
              )}
              {task.history?.slice().reverse().map((h, i) => (
                <div key={i} className="border border-white/5 rounded-md p-3 bg-black/30">
                  <div className="flex justify-between text-xs text-white/50 font-mono mb-2">
                    <span>{h.by_name}</span>
                    <span>{fmtDateTime(h.at)}</span>
                  </div>
                  <div className="space-y-1">
                    {Object.entries(h.previous || {}).map(([k, v]) => (
                      <div key={k} className="text-xs">
                        <span className="text-white/40 mr-2 overline">Prev {k}:</span>
                        <span className="text-white/80">{String(v) || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {isManager && onTogglePriority && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost" size="sm"
                data-testid={TID.taskPriorityBtn(task.id)}
                title={`Priority: ${prioMeta.label}`}
                aria-label={`Set priority (now ${prioMeta.label})`}
                className={`h-7 w-7 p-0 shrink-0 grid place-items-center ${prioMeta.text} hover:bg-white/5`}
              >
                <Flag size={12} fill={prio === "normal" || prio === "low" ? "none" : "currentColor"} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-surface border-white/10 text-white min-w-[140px]" align="start">
              {PRIORITY_ORDER.map((k) => (
                <DropdownMenuItem
                  key={k}
                  data-testid={`task-priority-set-${task.id}-${k}`}
                  onClick={() => onTogglePriority(task, k)}
                  className="text-xs cursor-pointer focus:bg-white/10"
                >
                  <Flag size={12} className={`mr-2 ${PRIORITY_META[k].text}`} fill="currentColor" />
                  {PRIORITY_META[k].label}
                  {prio === k && <span className="ml-auto text-white/40">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button
          variant="ghost" size="sm"
          data-testid={`task-comments-btn-${task.id}`}
          onClick={() => setCommentsOpen(true)}
          title="View / add comments"
          disabled={!!task._from_todo}
          className="h-7 px-1.5 text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-30"
        >
          <MessageSquare size={12} className="mr-1" /> Comments{commentCount > 0 && <span className="ml-1 text-[0.7rem] text-white/40">({commentCount})</span>}
        </Button>

        {isManager && onDelete && (
          <Button
            variant="ghost" size="sm"
            onClick={() => onDelete(task)}
            title="Delete task"
            aria-label="Delete task"
            className="h-7 w-7 p-0 shrink-0 grid place-items-center ml-auto text-white/40 hover:text-red-400 hover:bg-red-500/10"
          >
            <Trash2 size={12} />
          </Button>
        )}
      </div>

      <TaskSidePanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        task={{ ...task, comments: taskState.comments || task.comments }}
        status={displayStatus}
        assignee={assignee}
        currentUser={currentUser}
        isManager={isManager}
        canEdit={canEdit}
        onEdit={onEdit && !task._from_todo ? onEdit : null}
        onStatusChange={onStatusChange ? changeStatus : null}
        rawOnStatusChange={onStatusChange}
        onTogglePriority={onTogglePriority}
        onOpenComments={() => setCommentsOpen(true)}
        onCommentAdded={(u) => setTaskState(u)}
      />
      <TaskCommentsDialog
        task={taskState}
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        onCommentAdded={(updated) => setTaskState(updated)}
      />
    </div>
  );
}
